import * as React from "react";
import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { DataTablePagination } from "./data-table-pagination";
import NotFoundData from "components/common/NotFoundData";
import { cn } from "@/utils/shadcn-utils";
import { getCommonPinningStyles } from "@/lib/data-table";

interface DataTableProps<TData> extends React.ComponentProps<"div"> {
  table: TanstackTable<TData>;
  noDataMessage: string;
  withFooter?: boolean;
  className?: string;
  tableLayout?: "auto" | "fixed";
  /**
   * Custom footer renderer for complex footer structures
   * If provided, it will override the default footer rendering
   */
  customFooter?: (table: TanstackTable<TData>) => React.ReactNode;
  /**
   * Use TanStack's getFooterGroups() for automatic footer with nested columns
   * This respects the column group structure defined in column definitions
   */
  useFooterGroups?: boolean;
}

export function DataTable<TData>({
  table,
  children,
  noDataMessage,
  withFooter,
  tableLayout,
  className,
  customFooter,
  useFooterGroups = false,
}: DataTableProps<TData>) {
  return (
    <div className={cn("flex w-full flex-col gap-2.5 min-h-[570px] rounded-lg bg-white p-2 text-grayDataTable", className)}>
      {children}
      <div className="rounded-md flex-[1]">
        <Table tableLayout={tableLayout}>
          <TableHeader className="bg-blueCover rounded-tr-xl rounded-tl-xl">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      className="text-black3 text-sm font-medium"
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        ...getCommonPinningStyles({ column: header.column, withBorder: true }),
                      }}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} className="group">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="text-sm text-gray4 py-2"
                      style={{
                        ...getCommonPinningStyles({ column: cell.column, withBorder: true }),
                      }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="text-center h-[450px]">
                  <NotFoundData message={noDataMessage ?? "Aucune donnée"} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {withFooter && (
            <TableFooter>
              {customFooter ? (
                customFooter(table)
              ) : useFooterGroups ? (
                // Use TanStack's getFooterGroups for automatic nested footer structure
                table.getFooterGroups().map((footerGroup) => (
                  <TableRow key={footerGroup.id} className="font-semibold bg-gray-50">
                    {footerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        colSpan={header.colSpan}
                        className="text-sm"
                        style={{
                          ...getCommonPinningStyles({ column: header.column }),
                        }}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                // Default flat footer structure (backward compatible)
                <TableRow className="font-semibold bg-gray-50">
                  {table.getVisibleFlatColumns().map((column) => (
                    <TableCell
                      key={column.id}
                      className="text-sm"
                      style={{
                        ...getCommonPinningStyles({ column }),
                      }}>
                      {column.columnDef.footer ? flexRender(column.columnDef.footer, { column, table }) : null}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableFooter>
          )}
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
